import { Box, Divider, Tab, TabProps, Tabs, Theme, Typography, withStyles } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useUserInfo } from '@terragotech/gen5-shared-components';
import React, { useContext, useMemo, useState } from 'react';
import { useDataExportsModal } from '../contexts/dataExportContext';
import { useIntegrationsModal } from '../contexts/integrationContext';
import { LanguageContext } from '../contexts/LanguageContext/languageContext';
import { colors } from '../styles/theme';
import { LEFT_NAVIGATION_BAR_WIDTH, USERS_AND_ROLES_Z_INDEX } from '../utils/layers';
import { MOBILE_BREAKPOINT } from '../utils/utilityHelper';
import APIMessages from './APIMessagesContainer/APIMessagesContainer';
import DataExportTracking from './DataExportTrackingContainer/DataExportTrackingContainer';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}
const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <Typography
            component="div"
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}>
            {value === index && <Box>{children}</Box>}
        </Typography>
    );
};
const StyledTabs = withStyles((theme: Theme) => ({
    root: {
        minHeight: 39,
    },
    indicator: {
        backgroundColor: theme.palette.primary.main,
        height: '3px',
    },
}))(Tabs);

const StyledTab = withStyles((theme: Theme) => ({
    root: {
        textTransform: 'none',
        minWidth: 74,
        fontWeight: 400,
        color: colors.black0,
        minHeight: 39,
        height: 19,
        padding: 10,
        fontSize: 16,
        opacity: 1,
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        [theme.breakpoints.down(MOBILE_BREAKPOINT + 1)]: {
            fontSize: 14,
        },
    },
    selected: {},
}))((props: TabProps) => <Tab disableRipple {...props} />);

const useStyles = makeStyles(theme => ({
    root: {
        zIndex: USERS_AND_ROLES_Z_INDEX,
        position: 'fixed',
        left: 70,
        width: `calc(100vw - ${LEFT_NAVIGATION_BAR_WIDTH}px)`,
        height: '100vh',
        backgroundColor: colors.white,
        [theme.breakpoints.down(MOBILE_BREAKPOINT + 1)]: {
            width: '-webkit-fill-available',
            padding: '15px 18px 0px 19px',
            height: '100%',
            left: 0,
        },
        padding: '42px 55px 0px 49px',
    },
    initRoot: {
        minWidth: 0,
        textTransform: 'capitalize',
        color: colors.black0,
    },
    title: {
        color: colors.black0,
        fontStyle: 'normal',
        fontSize: 24,
        fontWeight: 500,
    },
    divider: {
        background: colors.black10,
    },
    titleContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: 19,
    },
    buttonContainer: {
        display: 'flex',
        width: 'auto',
        padding: '5px 12px',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 5,
        border: `1px solid ${theme.palette.primary.main}`,
        textTransform: 'capitalize',
        color: theme.palette.primary.main,
        fontSize: 15,
        fontWeight: 500,
        fontStyle: 'normal',
        lineHeight: 'normal',
        right: 6,
    },
    iconSize: {
        fontSize: '24px !important',
    },
}));

const Logs = () => {
    const classes = useStyles();
    const { translate } = useContext(LanguageContext);
    const [value, setValue] = useState(0);
    const { closeIntegrations } = useIntegrationsModal();
    const { closeDataExports } = useDataExportsModal();
    const { hasAdvancedLoggingAccess, hasDataExportTrackingAccess } = useUserInfo();

    const logOptions = useMemo(
        () => [
            {
                label: 'API Logging',
                component: APIMessages,
                permission: hasAdvancedLoggingAccess,
                onBack: closeIntegrations,
            },
            {
                label: 'Data Export Logging',
                component: DataExportTracking,
                permission: hasDataExportTrackingAccess,
                onBack: closeDataExports,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [hasAdvancedLoggingAccess, hasDataExportTrackingAccess]
    );

    const allowedLogOptions = useMemo(() => logOptions.filter(option => option.permission), [logOptions]);

    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
    };

    return (
        <div className={classes.root}>
            <div className={classes.titleContainer}>
                <Typography className={classes.title}>{translate('Logs')}</Typography>
            </div>
            {allowedLogOptions.length > 0 && (
                <>
                    <StyledTabs value={value} onChange={handleChange} aria-label="logs tabs">
                        {allowedLogOptions.map((option, index) => (
                            <StyledTab key={option.label} label={translate(option.label)} />
                        ))}
                    </StyledTabs>
                    <Divider className={classes.divider} />
                    {allowedLogOptions.map((option, index) => (
                        <TabPanel value={value} index={index} key={option.label}>
                            <option.component onBack={option.onBack} />
                        </TabPanel>
                    ))}
                </>
            )}
        </div>
    );
};

export default Logs;
